import React, { useState, useEffect, useRef, useCallback } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { SlidingButton } from "../styledComponents";
import styled from "styled-components";
import { FaHouse } from "react-icons/fa6";
import { housesDict } from "../pages/HouseInfo";
import { Link } from "react-router-dom";
import { GoDotFill } from "react-icons/go";

const ImageContainer = styled.div`
  width: 100%; /* Full viewport width */
  height: 100vh; /* Full viewport height */
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
`;

const PinWrapper = styled.div`
  position: absolute;
  z-index: 1;

  &:hover {
    z-index: 10;
  }

  &:hover > span {
    opacity: 1;
    transform: translateX(-50%) translateY(-4px);
  }
`;

const Tooltip = styled.span`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(0px);
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 0.75rem;
  white-space: nowrap;
  padding: 5px 10px;
  border-radius: 6px;
  pointer-events: none;
  opacity: 0;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.75);
  }
`;

const MapButton = styled.button`
  position: absolute;
  height: 25px;
  width: 25px;
  background-color: #ff0000;
  z-index: 2;
  cursor: pointer;
  border: none;
  border-radius: 50%;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.3);
    box-shadow: 0 0 8px rgba(255, 0, 0, 0.6);
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.15s ease-out,
    visibility 0.15s ease-out,
    background 0.15s ease-out;
`;

const ModalContent = styled.div`
  position: relative;
  width: 600px;
  max-width: 92vw;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  background-color: ${({ theme }) => theme.backgroundColor};

  @media (max-width: 768px) {
    width: 92vw;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 220px;
  object-fit: cover;
  display: block;
`;

const ModalBody = styled.div`
  padding: 20px 24px 24px;
`;

const ModalTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1.15rem;
  color: ${({ theme }) => theme.textColors.primary};
  margin: 0 0 10px;
  padding-right: 28px;
  line-height: 1.4;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 0.9rem;
  color: ${({ theme }) => theme.textColors.secondary};
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const Description = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1rem;
  color: ${({ theme }) => theme.textColors.primary};
  line-height: 1.55;
  margin: 0 0 20px;
`;

const CloseIcon = styled(IoCloseOutline)`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: #fff;
  font-size: 1.8em;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 50%;
  padding: 2px;
  z-index: 1;
  &:hover {
    background: rgba(0, 0, 0, 0.65);
  }
`;

// Define a type for the button position
type ButtonPosition = {
  id: string;
  top: number;
  left: number;
};

// Define features at module scope so callbacks don't require it as a dependency
// Pins carry only a property id and a position; the label comes from
// HouseInfo so the map can never drift from the nav and footer, and no unit
// number ends up in the bundle.
const features = [
  { id: "C001", x: 2000, y: 2175 },
  { id: "C004", x: 2255, y: 2175 },
  { id: "C003", x: 2200, y: 2150 },
  { id: "C005", x: 1900, y: 2050 },
  { id: "C002", x: 2200, y: 2200 },
  { id: "C204", x: 2255, y: 2120 },
].map((pin) => ({ ...pin, label: housesDict[pin.id].displayName }));

const DESCRIPTION_LIMIT = 180;

function Map() {
  const [buttonPositions, setButtonPositions] = useState<ButtonPosition[]>([]);
  const [activeFeature, setActiveFeature] = useState<string | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [modalOpacity, setModalOpacity] = useState(0);

  const updateButtonPositions = useCallback(() => {
    const image = imageRef.current;
    if (!image) return;

    const originalImageWidth = 3400; // Original width
    const originalImageHeight = 4200; // Original height

    const containerWidth = image.clientWidth;
    const containerHeight = image.clientHeight;

    const scale = Math.max(
      containerWidth / originalImageWidth,
      containerHeight / originalImageHeight,
    );

    const newButtonPositions = features.map((feature) => {
      const displayedImageWidth = originalImageWidth * scale;
      const displayedImageHeight = originalImageHeight * scale;

      const croppedWidth = Math.max(
        0,
        (displayedImageWidth - containerWidth) / 2,
      );
      const croppedHeight = Math.max(
        0,
        (displayedImageHeight - containerHeight) / 2,
      );

      const visibleFeatureX = feature.x * scale - croppedWidth;
      const visibleFeatureY = feature.y * scale - croppedHeight;

      return {
        id: feature.id,
        top: visibleFeatureY,
        left: visibleFeatureX,
      };
    });

    setButtonPositions(newButtonPositions);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      updateButtonPositions();
    };

    window.addEventListener("resize", handleResize);
    // Update position once the image is loaded
    if (imageRef.current) {
      imageRef.current.onload = updateButtonPositions;
    }

    // Clean up event listener
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [updateButtonPositions]);

  const TRANSITION_MS = 500;

  useEffect(() => {
    if (activeFeature === null) return;
    const onScroll = () => handleClose();
    window.addEventListener("scroll", onScroll, { once: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [activeFeature]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = (featureId: string) => {
    setActiveFeature(featureId);
    setModalOpacity(0);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setModalOpacity(1);
      });
    });
  };

  const handleClose = () => {
    setModalOpacity(0);
    setTimeout(() => setActiveFeature(null), TRANSITION_MS);
  };

  return (
    <div className="App">
      <ImageContainer>
        <img ref={imageRef} src="higherres.jpeg" alt="Map" />
        {buttonPositions.map((position) => (
          <PinWrapper
            key={position.id}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
          >
            <Tooltip>
              {features.find((f) => f.id === position.id)?.label}
            </Tooltip>
            <MapButton onClick={() => handleClick(position.id)}>
              <FaHouse />
            </MapButton>
          </PinWrapper>
        ))}
      </ImageContainer>
      {activeFeature !== null && (
        <ModalBackdrop
          onClick={handleClose}
          style={{
            opacity: modalOpacity,
            visibility: modalOpacity > 0 ? "visible" : "hidden",
          }}
        >
          <ModalContent
            onClick={(e: React.MouseEvent<HTMLDivElement>) =>
              e.stopPropagation()
            }
            style={{ opacity: modalOpacity }}
          >
            <CloseIcon onClick={handleClose} />
            <HeroImage src={housesDict[activeFeature].flagPhoto} alt="house" />
            <ModalBody>
              <ModalTitle>{housesDict[activeFeature].tagline}</ModalTitle>
              <StatsRow>
                {housesDict[activeFeature].allowed_guests} Guests
                <GoDotFill size="0.5rem" />
                {housesDict[activeFeature].num_bedrooms} Bedrooms
                <GoDotFill size="0.5rem" />
                {housesDict[activeFeature].num_beds} Beds
                <GoDotFill size="0.5rem" />
                {housesDict[activeFeature].num_baths} Baths
              </StatsRow>
              <Description>
                {housesDict[activeFeature].description.length >
                DESCRIPTION_LIMIT
                  ? housesDict[activeFeature].description
                      .slice(0, DESCRIPTION_LIMIT)
                      .trimEnd() + "…"
                  : housesDict[activeFeature].description}
              </Description>
              <SlidingButton style={{ verticalAlign: "middle" }}>
                <Link to={housesDict[activeFeature].route}>
                  <span>View details</span>
                </Link>
              </SlidingButton>
            </ModalBody>
          </ModalContent>
        </ModalBackdrop>
      )}
    </div>
  );
}

export default Map;
